import type { Schema } from '../../amplify/data/resource';

// Model types derived from the Gen 2 schema
export type BusinessAdmin = Schema['BusinessAdmin']['type'];
export type Company = Schema['Company']['type'];
export type CompanyOwner = Schema['CompanyOwner']['type'];
export type User = Schema['User']['type'];
export type RegionalDivision = Schema['RegionalDivision']['type'];
export type Location = Schema['Location']['type'];
export type Form = Schema['Form']['type'];
export type Equipment = Schema['Equipment']['type'];
export type EquipmentCategory = Schema['EquipmentCategory']['type'];
export type SingleUnit = Schema['SingleUnit']['type'];
export type FormTemplate = Schema['FormTemplate']['type'];
export type FormTemplateGroup = Schema['FormTemplateGroup']['type'];
export type Discrepancy = Schema['Discrepancy']['type'];
export type ArchivedDiscrepancy = Schema['ArchivedDiscrepancy']['type'];
export type Report = Schema['Report']['type'];
export type ResourceLibrary = Schema['ResourceLibrary']['type'];
export type Document = Schema['Document']['type'];
export type Video = Schema['Video']['type'];
export type SensitiveUserInfo = Schema['SensitiveUserInfo']['type'];
export type PMModuleContent = Schema['PMModuleContent']['type'];
export type PMConfiguration = Schema['PMConfiguration']['type'];
export type PMServiceLevel = Schema['PMServiceLevel']['type'];
export type PMPartsAndProcedure = Schema['PMPartsAndProcedure']['type'];
export type PMNotificationSetting = Schema['PMNotificationSetting']['type'];
export type PMReport = Schema['PMReport']['type'];
export type VendorList = Schema['VendorList']['type'];
export type ServiceCenter = Schema['ServiceCenter']['type'];
export type LaborCategory = Schema['LaborCategory']['type'];
export type LaborRate = Schema['LaborRate']['type'];
export type ExpenseRecord = Schema['ExpenseRecord']['type'];
export type ExpenseCategory = Schema['ExpenseCategory']['type'];
export type UnitExpenseBudget = Schema['UnitExpenseBudget']['type'];
export type Tire = Schema['Tire']['type'];
export type TireReport = Schema['TireReport']['type'];
export type AxleBlueprint = Schema['AxleBlueprint']['type'];
export type InspectionReport = Schema['InspectionReport']['type'];
export type DailyServiceReport = Schema['DailyServiceReport']['type'];
export type ShiftReport = Schema['ShiftReport']['type'];
export type DOTInspectionReport = Schema['DOTInspectionReport']['type'];
export type UserSkillReport = Schema['UserSkillReport']['type'];
export type ReportAnswer = Schema['ReportAnswer']['type'];
export type SavedReportConfiguration = Schema['SavedReportConfiguration']['type'];
export type UserReportFavorite = Schema['UserReportFavorite']['type'];
export type TrainingTopic = Schema['TrainingTopic']['type'];
export type TrainingPlan = Schema['TrainingPlan']['type'];
export type TrainingSchedule = Schema['TrainingSchedule']['type'];
export type TrainingEnrollment = Schema['TrainingEnrollment']['type'];
export type TrainingCompletion = Schema['TrainingCompletion']['type'];
export type TrainingAffidavit = Schema['TrainingAffidavit']['type'];
export type LibraryPlanTemplate = Schema['LibraryPlanTemplate']['type'];
export type LibraryTopicTemplate = Schema['LibraryTopicTemplate']['type'];
export type HelpSupportDoc = Schema['HelpSupportDoc']['type'];
export type CompanyResource = Schema['CompanyResource']['type'];
export type CompanyNote = Schema['CompanyNote']['type'];
export type BillingRate = Schema['BillingRate']['type'];

// ============================================================================
// Types NOT in the Gen 2 schema — defined manually or re-exported from API.ts
// These are either nested types, input types, or models pending Gen 2 migration
// ============================================================================

// District model (exists in Gen 1 schema, not yet in Gen 2 data/resource.ts)
export type District = {
    __typename: 'District';
    id: string;
    name: string;
    companyId: string;
    regionalDivisionId: string;
    description?: string | null;
    createdAt: string;
    updatedAt: string;
    owner?: string | null;
};

// SafetyLesson nested type (stored as JSON array on User model)
export type SafetyLesson = {
    id?: string | null;
    type?: string | null;
    name?: string | null;
    dateCompleted?: string | null;
    signature?: string | null;
};

export type SafetyLessonInput = {
    id?: string | null;
    type?: string | null;
    name?: string | null;
    dateCompleted?: string | null;
    signature?: string | null;
};

// Sort direction enum
export enum ModelSortDirection {
    ASC = 'ASC',
    DESC = 'DESC',
}

// ============================================================================
// Input types for GraphQL mutations
// ============================================================================

export type CreateCompanyOwnerInput = {
    id?: string | null;
    email: string;
    name: string;
    subscriptionType: string;
    role?: string | null;
    permissions?: string | null;
    companyId?: string | null;
    cognitoId?: string | null;
    domain?: string | null;
    siteAdmin?: boolean | null;
    discrepancyNotification?: boolean | null;
    discrepancyNotificationTypes?: string | null;
    pmNotification?: boolean | null;
    pmNotificationTypes?: string | null;
    trainingNotification?: boolean | null;
    trainingNotificationTypes?: string | null;
};

export type CreateBusinessAdminInput = {
    id?: string | null;
    email: string;
    name: string;
    domain?: string | null;
    cognitoId: string;
    role: string;
    permissions?: string | null;
};

export type CreateCompanyInput = {
    id?: string | null;
    name: string;
    subscriptionType: string;
    subscriptionExpiration: string;
    maxNumberUsers: number;
    maxNumberEquipment: number;
    companyOwnerId?: string | null;
    [key: string]: unknown;
};

export type CreateUserInput = {
    id?: string | null;
    name: string;
    email: string;
    companyId?: string | null;
    cognitoId?: string | null;
    domain?: string | null;
    role?: string | null;
    permissions?: string | null;
    [key: string]: unknown;
};

export type UpdateUserInput = {
    id: string;
    [key: string]: unknown;
};

export type CreateEquipmentCategoryInput = {
    id?: string | null;
    name: string;
    companyId: string;
    [key: string]: unknown;
};

export type UpdateEquipmentCategoryInput = {
    id: string;
    [key: string]: unknown;
};

export type CreateTireInput = {
    id?: string | null;
    companyId: string;
    [key: string]: unknown;
};

export type UpdateFormInput = {
    id: string;
    [key: string]: unknown;
};

export type CreateArchivedDiscrepancyInput = {
    id?: string | null;
    [key: string]: unknown;
};
